import * as React from "react";
import * as Dialog from "@radix-ui/react-dialog";
import { X } from "lucide-react";
import type {
  OeeBreakdownDetail,
  OeeMetricBreakdown,
  OeeConfidenceBreakdown,
} from "@/types/oee.types";

type MetricKey = keyof OeeBreakdownDetail;

const METRIC_TITLE: Record<MetricKey, string> = {
  availability: "Disponibilidade",
  performance: "Desempenho",
  quality: "Qualidade",
  confidence: "Confiança",
  oee: "OEE — Eficiência geral",
};

// Estes rótulos são EXIBIDOS junto da fórmula que o backend manda em
// `oee-breakdown.ts`. Quando a calendarização mudou o significado de `plannedMinutes` e removeu o
// teto de 100% da Disponibilidade, dois deles passaram a dizer o contrário da fórmula ao lado — o
// diálogo que existe para impedir a leitura errada era quem estava produzindo uma.
const INPUT_LABEL: Record<string, string> = {
  plannedMinutes:
    "Tempo planejado (min) — expediente da linha (ou a janela da ordem, sem expediente)",
  operatingMinutes: "Tempo operando (min) — overlap real de produção",
  downtimeMinutes:
    "STOPs registrados (min) — informativo: ocorrem dentro do tempo operando e não reduzem a Disponibilidade",
  timeLostOutsideStops:
    "Tempo planejado não operado (min) — partida atrasada, encerramento antecipado ou ociosidade (reduz a Disponibilidade)",
  overrunMinutes:
    "Operou além do plano (min) — hora extra: é o que leva a Disponibilidade acima de 100%",
  plannedQty: "Quantidade planejada (peças)",
  idealRatePerMin: "Ritmo ideal (peças/min) — Quantidade planejada ÷ tempo planejado",
  producedQty: "Peças produzidas",
  totalLoss: "Peças com perda (refugo + desperdício + retrabalho)",
  goodCount: "Peças boas",
  runCount: "Execuções no período",
  nodataMachineRatio: "Proporção de máquinas sem dados",
  nodataMinutes: "Minutos sem leitura de sensores",
  hasManualEntries: "Houve entradas manuais?",
  availability: "Disponibilidade (%)",
  performance: "Desempenho (%)",
  quality: "Qualidade (%)",
};

function formatInputValue(key: string, value: number | string | boolean | null): string {
  if (value === null) return "—";
  if (typeof value === "boolean") return value ? "Sim" : "Não";
  if (typeof value === "number") {
    if (key === "nodataMachineRatio") return `${(value * 100).toFixed(1)}%`;
    if (key === "availability" || key === "performance" || key === "quality") {
      return `${value.toFixed(1)}%`;
    }
    if (key === "idealRatePerMin") return `${value.toLocaleString("pt-BR")} un/min`;
    return value.toLocaleString("pt-BR");
  }
  return value;
}

function formatComputed(
  metric: MetricKey,
  data: OeeMetricBreakdown | OeeConfidenceBreakdown
): string {
  if (metric === "confidence") {
    const conf = data as OeeConfidenceBreakdown;
    return {
      HIGH: "Alta",
      MEDIUM: "Média",
      LOW: "Baixa",
      INSUFFICIENT: "Insuficiente",
    }[conf.computedLevel];
  }
  const m = data as OeeMetricBreakdown;
  return m.computed === null ? "—" : `${m.computed.toFixed(1)}%`;
}

interface Props {
  metric: MetricKey | null;
  breakdown: OeeBreakdownDetail | undefined;
  periodLabel: string;
  open: boolean;
  onClose: () => void;
}

export function OeeMetricExplanationDialog({
  metric,
  breakdown,
  periodLabel,
  open,
  onClose,
}: Props): React.ReactElement | null {
  if (!metric || !breakdown) return null;
  const data = breakdown[metric];
  const inputs = Object.entries(data.inputs);

  return (
    <Dialog.Root open={open} onOpenChange={(o) => !o && onClose()}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 z-[60] animate-fade-in bg-background/80" />
        <Dialog.Content className="fixed left-1/2 top-1/2 z-[61] w-full max-w-md -translate-x-1/2 -translate-y-1/2 animate-scale-in rounded-lg border bg-card p-5 shadow-lg focus:outline-none">
          <div className="mb-3 flex items-start justify-between gap-2">
            <Dialog.Title className="text-base font-semibold text-text-primary">
              {METRIC_TITLE[metric]}
            </Dialog.Title>
            <Dialog.Close className="rounded p-1 text-text-tertiary hover:text-text-primary focus-visible:outline-none focus-visible:[box-shadow:var(--rd-shadow-focus)]">
              <X className="h-4 w-4" />
              <span className="sr-only">Fechar</span>
            </Dialog.Close>
          </div>

          <Dialog.Description className="mb-4 text-[13px] text-text-secondary">
            {data.formula}
          </Dialog.Description>

          <div className="space-y-3">
            <div>
              <p className="text-[11px] uppercase tracking-wider text-text-tertiary">
                Janela considerada
              </p>
              <p className="mt-0.5 text-[13px] font-medium text-text-primary">{periodLabel}</p>
            </div>

            <div>
              <p className="mb-1.5 text-[11px] uppercase tracking-wider text-text-tertiary">
                Valores usados no cálculo
              </p>
              <dl className="bg-bg-muted-rd/40 divide-y divide-border-subtle rounded border text-[12px]">
                {inputs.length === 0 ? (
                  <p className="p-2 italic text-text-tertiary">
                    Sem dados suficientes para esta métrica.
                  </p>
                ) : (
                  inputs.map(([key, value]) => (
                    <div key={key} className="flex justify-between gap-2 px-2 py-1.5">
                      <dt className="text-text-secondary">{INPUT_LABEL[key] ?? key}</dt>
                      <dd className="font-mono text-text-primary">
                        {formatInputValue(key, value)}
                      </dd>
                    </div>
                  ))
                )}
              </dl>
            </div>

            <div className="border-t border-border-subtle pt-3">
              <p className="text-[11px] uppercase tracking-wider text-text-tertiary">Resultado</p>
              <p className="mt-0.5 font-mono text-[18px] font-semibold text-text-primary">
                {formatComputed(metric, data)}
              </p>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}
